import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import authApiService from "../../services/auth-api.service";
import operatorApiService from "../../services/operator-api.service";
import { initMqttClient } from "../../utils/mqtt-client";
import { initJanusPublisher } from "../../utils/observeVideo";
import {
  recvDataUpdateScreen,
  recvDataSetDepartureAnnounce,
} from "../../utils/recvData";
import {
  shouldRedirectToAuth,
  redirectToAuth,
  getYmcAccessToken,
  setSendDataCallback,
  setMCId,
  setVehicleCommunicationCodes,
  setModalCallback,
  ymcCleanup,
} from "../../utils/ymc-client";
import { isEiheijiEnvironment } from "../../utils/environment";
import {
  ObserveInitData,
  ObserveRefreshData,
  Vehicle,
} from "../../types/observe";

import HeaderObserve from "../../components/header/HeaderObserve";
import VehiclePanel from "./components/vehiclePanel/VehiclePanel";
import ObserveFooter from "./components/observeFooter/ObserveFooter";
import MessageModal from "../../components/messageModal/MessageModal";
import "./Observe.scss";

const Observe = () => {
  const navigate = useNavigate();
  const mqttClientRef = useRef<any | null>(null);
  const janusHandlesRef = useRef<Record<string, any>>({});

  const [initData, setInitData] = useState<ObserveInitData | null>(null);
  const [refreshDataMap, setRefreshDataMap] = useState<
    Record<string, ObserveRefreshData>
  >({});
  const [userName, setUserName] = useState("");
  const [baseName, setBaseName] = useState("");
  const [baseId, setBaseId] = useState<number | null>(null);
  const [monitorConsoleId, setMonitorConsoleId] = useState<number | null>(null);
  const [monitorConsoleNo, setMonitorConsoleNo] = useState<string | null>(null);
  const [observeCC, setObserveCC] = useState("");
  const [onevehicleCC, setOnevehicleCC] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [janusServer, setJanusServer] = useState<string | null>(null);
  const [janusSdkServer, setJanusSdkServer] = useState<string | null>(null);
  const [sdkServerUserInfo, setSdkServerUserInfo] = useState<any | null>(null);
  const [departureAnnounceMap, setDepartureAnnounceMap] = useState<
    Record<number, boolean>
  >({});
  const departureAnnounceTimersRef = useRef<Record<number, number>>({});

  useEffect(() => {
    const fetchMe = async () => {
      try {
        const user = await authApiService.me();

        if (user.role_cd !== "04") {
          navigate("/login");
          return;
        }

        // 永平寺環境のみ:YMC管制初期化
        if (isEiheijiEnvironment()) {
          if (shouldRedirectToAuth()) {
            redirectToAuth();
            return;
          }

          getYmcAccessToken();
          console.log("getYmcAccessToken Done");
        }

        const response = await operatorApiService.doGetCommunicationCode({
          role_cd: "01",
          monitor_console_id: user.monitor_console_id,
        });
        setOnevehicleCC(response);

        setUserName(user.user_name);
        setBaseName(user.base_name);
        setMonitorConsoleId(user.monitor_console_id);
        setMonitorConsoleNo(user.monitor_console_no);
        setBaseId(user.base_id);
        setObserveCC(user.communication_code);

        // 永平寺環境のみ:YMC コールバック設定
        if (isEiheijiEnvironment()) {
          setSendDataCallback(sendData, user.communication_code);
          setMCId(user.monitor_console_id);
          setModalCallback((message: string) => {
            setMessage(message);
            setIsModalOpen(true);
            setTimeout(() => setIsModalOpen(false), 3000);
          });
        }
      } catch (error) {
        navigate("/login");
      }
    };

    fetchMe();
  }, [navigate]);

  const fetchInitData = async () => {
    if (!monitorConsoleId || !baseId) return;

    try {
      const response = await operatorApiService.doGetInitDataForObserve({
        monitorConsoleId,
        baseId,
      });

      setInitData(response);

      // 永平寺環境のみ:車両通信コード情報をYMCクライアントに設定
      if (isEiheijiEnvironment() && response.vehicles) {
        // layout_idの順番で車両を並べ替え（1,2,3,4の順）
        const sortedVehicles = response.vehicles
          .filter(
            (vehicle: any) => vehicle.layout_id >= 1 && vehicle.layout_id <= 4,
          )
          .sort((a: any, b: any) => a.layout_id - b.layout_id);

        const vehicleCommunicationCodes = sortedVehicles.map(
          (vehicle: any, index: number) => ({
            vehicle_communication_code: vehicle.vehicle_communication_code,
            receivedTimeLast: new Date().getTime(), // 初期値として現在時刻を設定
            index: index, // layout_id順でのインデックス(0-3)
          }),
        );
        setVehicleCommunicationCodes(vehicleCommunicationCodes);
      }
    } catch (error) {
      console.error("初期データ取得エラー:", error);
    }
  };

  useEffect(() => {
    fetchInitData();
  }, [baseId]);

  useEffect(() => {
    return () => {
      // 永平寺環境のクリーンアップ
      if (isEiheijiEnvironment()) {
        ymcCleanup();
      }
    };
  }, []);

  useEffect(() => {
    if (!baseId) return;

    const fetchJanusConfig = async () => {
      try {
        const janusConfig = await operatorApiService.doGetJanusConfig({
          baseId,
        });

        setJanusServer(janusConfig.janusServer);
        setJanusSdkServer(janusConfig.janusSdkServer);
        setSdkServerUserInfo({ credential: janusConfig.credential });
      } catch (err) {
        console.error("Janus設定の取得に失敗しました", err);
      }
    };

    fetchJanusConfig();
  }, [baseId]);

  useEffect(() => {
    const mqttRoleId = import.meta.env.VITE_MQTT_OBSERVE_ROLE_ID;
    const mqttClient = initMqttClient(mqttRoleId, {
      onMessage: async (json) => {
        const updateData = recvDataUpdateScreen(json);
        if (updateData && updateData.dt === observeCC) {
          if (updateData.cmd === 1) {
            const responseMessage =
              await operatorApiService.doGetMessageModalInfo({
                vehicleId: updateData.vid,
                messageId: updateData.prm,
              });

            setMessage(responseMessage.message);
            setIsModalOpen(true);
            setTimeout(() => setIsModalOpen(false), 3000);
          } else if (updateData.cmd === 0) {
            fetchInitData();
          }
        }

        // Modem_Info更新は廃止。

        // 発車前アナウンス指示受信時の処理
        if (isEiheijiEnvironment()) {
          const dataSetDepartureAnnounce = recvDataSetDepartureAnnounce(json);
          if (
            dataSetDepartureAnnounce &&
            dataSetDepartureAnnounce.destination === observeCC
          ) {
            // 車両IDを特定
            const vehicle = initData?.vehicles.find(
              (v) =>
                v.vehicle_communication_code ===
                dataSetDepartureAnnounce.source,
            );
            if (!vehicle) return;

            const vehicleId = vehicle.vehicles_vehicle_id;

            // [発進]ボタンの背景色を変更
            if (dataSetDepartureAnnounce.control === 1) {
              // ボタン背景色を黄色の押下状態にする(最大で15秒)
              setDepartureAnnounceMap((prev) => ({
                ...prev,
                [vehicleId]: true,
              }));
              if (departureAnnounceTimersRef.current[vehicleId]) {
                clearTimeout(departureAnnounceTimersRef.current[vehicleId]);
              }
              departureAnnounceTimersRef.current[vehicleId] = setTimeout(() => {
                setDepartureAnnounceMap((prev) => ({
                  ...prev,
                  [vehicleId]: false,
                }));
              }, 15000);
            } else if (dataSetDepartureAnnounce.control === 0) {
              // ボタン背景色を元に戻す
              setDepartureAnnounceMap((prev) => ({
                ...prev,
                [vehicleId]: false,
              }));
              if (departureAnnounceTimersRef.current[vehicleId]) {
                clearTimeout(departureAnnounceTimersRef.current[vehicleId]);
              }
            }
          }
        }
      },
    });

    mqttClientRef.current = mqttClient;

    return () => {
      mqttClient.end(true);
      Object.values(departureAnnounceTimersRef.current).forEach(clearTimeout);
    };
  }, [initData]);

  const sendData = (msg: object) => {
    if (!mqttClientRef.current) {
      console.warn("MQTT client not connected");
      return;
    }

    console.log("sendData", msg);

    mqttClientRef.current.publish(
      import.meta.env.VITE_MQTT_TOPIC,
      JSON.stringify(msg),
      (err: any) => {
        if (err) {
          console.error("MQTT publish error:", err);
        } else {
          console.log("MQTT publish success");
        }
      },
    );
  };

  useEffect(() => {
    if (
      !janusServer ||
      !janusSdkServer ||
      !sdkServerUserInfo ||
      !initData?.vehicles
    )
      return;

    initData.vehicles
      .filter(
        (vehicle) =>
          vehicle.layout_id &&
          vehicle.layout_id >= 1 &&
          vehicle.layout_id <= 4 &&
          vehicle.zao_suid,
      )
      .forEach((vehicle) => {
        const vehicleId = vehicle.vehicles_vehicle_id;
        if (janusHandlesRef.current[vehicleId]) return;

        const suid = vehicle.zao_suid;
        const frequencyHz = monitorConsoleId === 1 ? 20000 : 20500;
        initJanusPublisher({
          janusServer,
          janusSdkServer,
          sdkServerUserInfo,
          suid,
          frequencyHz,
          onError: (err) => {
            console.error(`Janus音声送信エラー(${suid})`, err);
          },
          onPublish: (handle) => {
            janusHandlesRef.current[vehicleId] = handle;
            console.log(`Janus音声送信開始(${vehicleId})`);
          },
        });
      });
  }, [janusServer, janusSdkServer, sdkServerUserInfo, initData]);

  const handleReconnectJanus = (
    vehicleId: number,
    action: "connect" | "disconnect",
  ) => {
    if (
      !janusServer ||
      !janusSdkServer ||
      !sdkServerUserInfo ||
      !initData?.vehicles
    )
      return;

    if (action === "disconnect") {
      const handle = janusHandlesRef.current[vehicleId];
      if (handle) {
        handle.detach?.();
      }
    } else if (action === "connect") {
      const vehicle = initData?.vehicles.find(
        (v) => v.vehicles_vehicle_id === vehicleId,
      );
      if (!vehicle?.zao_suid) return;
      initJanusPublisher({
        janusServer,
        janusSdkServer,
        sdkServerUserInfo,
        suid: vehicle.zao_suid,
        onError: (err) => {
          console.error(`Janus再接続エラー(${vehicleId})`, err);
        },
        onPublish: (handle) => {
          janusHandlesRef.current[vehicleId] = handle;
          console.log(`Janus再接続完了(${vehicleId})`);
        },
      });
    }
  };

  const openModal = (msg: string) => {
    setMessage(msg);
    setIsModalOpen(true);
    setTimeout(() => setIsModalOpen(false), 3000);
  };

  const fetchAllRefreshData = async () => {
    if (!initData?.vehicles) return;

    const vehicleMonitorPairs = initData.vehicles.map((v) => ({
      vcc: v.vehicle_communication_code,
      monitor_console_id: v.monitor_console_id,
    }));

    try {
      const responseDataMap: Record<string, ObserveRefreshData> =
        await operatorApiService.doGetAllRefreshDataForObserve(
          vehicleMonitorPairs,
        );

      setRefreshDataMap(responseDataMap);
    } catch (err) {
      console.error("リフレッシュデータ取得エラー", err);
    }
  };

  useEffect(() => {
    fetchAllRefreshData();
    const interval = setInterval(fetchAllRefreshData, 3000);
    return () => clearInterval(interval);
  }, [initData]);

  const layoutVehicleMap = useMemo(() => {
    const map: Record<number, Vehicle | null> = {};
    for (let i = 1; i <= 4; i++) {
      map[i] = initData?.vehicles.find((v) => v.layout_id === i) ?? null;
    }
    return map;
  }, [initData]);

  const selectableVehicles = useMemo(
    () => initData?.vehicles.filter((v) => v.layout_id === null) ?? [],
    [initData],
  );

  const displayedVehicles = useMemo(
    () =>
      Object.entries(layoutVehicleMap).map(([layoutIdStr, vehicle]) => {
        const layoutId = Number(layoutIdStr);
        const refreshData = vehicle
          ? (refreshDataMap[vehicle.vehicle_communication_code] ?? null)
          : null;
        return { layoutId, vehicle, refreshData };
      }),
    [layoutVehicleMap, refreshDataMap],
  );

  const isAnyRemoteMode = useMemo(() => {
    return Object.values(refreshDataMap).some(
      (data) =>
        data?.layout != null &&
        (data?.rai?.assist_enable === "1" ||
          (data?.drive?.monitor_f === "1" && data?.drive?.drive_mode === 8)),
    );
  }, [refreshDataMap]);

  return (
    <>
      <div className="observeScreen">
        <HeaderObserve
          userName={userName}
          baseName={baseName}
          monitorConsoleNo={monitorConsoleNo ?? ""}
        />
        <div className="observeContents">
          <div className="observeGrid">
            {displayedVehicles.map(({ layoutId, vehicle, refreshData }) => (
              <VehiclePanel
                key={layoutId}
                layoutId={layoutId}
                vehicle={vehicle}
                announcement={initData?.announcement ?? []}
                selectableVehicles={selectableVehicles}
                refreshData={refreshData}
                monitorConsoleId={monitorConsoleId}
                observeCC={observeCC}
                onevehicleCC={onevehicleCC}
                sendData={sendData}
                onModalOpen={openModal}
                onReconnectJanus={handleReconnectJanus}
                isDepartureAnnounceActive={
                  vehicle
                    ? !!departureAnnounceMap[vehicle.vehicles_vehicle_id]
                    : false
                }
                isAnyRemoteMode={isAnyRemoteMode}
              />
            ))}
          </div>
          <div className="observeFooter">
            <ObserveFooter
              refreshDataMap={refreshDataMap}
              monitorConsoleId={monitorConsoleId}
              layoutVehicles={Object.values(layoutVehicleMap)}
              observeCC={observeCC}
              onevehicleCC={onevehicleCC}
              sendData={sendData}
              isAnyRemoteMode={isAnyRemoteMode}
            />
          </div>
        </div>
      </div>
      <MessageModal
        message={message}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};

export default Observe;
